/**
 * Niet in het origineel: het inlogscherm van de spelleider. Het origineel had op /admin geen
 * login. `AlleenSpelleider` toont dit scherm op de gevraagde URL zolang er geen sessie is;
 * na het inloggen verschijnt daar de gevraagde pagina.
 */
import { useRef, useState, type FormEvent } from 'react';
import { Alert, Button, Field, Flex, Input, Text, chakra } from '@chakra-ui/react';
import { isEmailAdres, normaliseerEmail, type Spelleider } from '@ame/shared';
import { inloggen } from '@/spel/auth-api';
import { tekst } from './hulp';
import { Formulierfout, Formulierkop, Inlogkader, Naar, Voetregel } from './Inlogkader';
import { WachtwoordInvoer } from './Wachtwoordveld';

type Props = {
  onIngelogd: (spelleider: Spelleider) => void;
  /** De sessie verliep terwijl de pagina open stond. */
  verlopen?: boolean;
};

export function Inlogscherm({ onIngelogd, verlopen = false }: Props) {
  const [email, setEmail] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [emailFout, setEmailFout] = useState<string>();
  const [wachtwoordFout, setWachtwoordFout] = useState<string>();
  const [fout, setFout] = useState<string>();
  const [bezig, setBezig] = useState(false);
  const emailVeld = useRef<HTMLInputElement>(null);
  const wachtwoordVeld = useRef<HTMLInputElement>(null);

  async function logIn(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    const nieuweEmailFout = !email.trim()
      ? 'Vul je e-mailadres in.'
      : isEmailAdres(email)
        ? undefined
        : 'Dit is geen geldig e-mailadres.';
    const nieuweWachtwoordFout = wachtwoord ? undefined : 'Vul je wachtwoord in.';
    setEmailFout(nieuweEmailFout);
    setWachtwoordFout(nieuweWachtwoordFout);
    if (nieuweEmailFout || nieuweWachtwoordFout) {
      (nieuweEmailFout ? emailVeld : wachtwoordVeld).current?.focus();
      return;
    }

    setBezig(true);
    setFout(undefined);
    try {
      onIngelogd(await inloggen(normaliseerEmail(email), wachtwoord));
    } catch (reden) {
      // Eén melding voor een onbekend adres en een fout wachtwoord, zoals de server die geeft.
      setFout(tekst(reden));
      setBezig(false);
      wachtwoordVeld.current?.select();
    }
  }

  return (
    <Inlogkader>
      <chakra.form
        onSubmit={logIn}
        noValidate
        display="flex"
        flexDirection="column"
        gap="5"
        colorPalette="ameBlue"
      >
        <Formulierkop titel="Inloggen">Met je e-mailadres en wachtwoord.</Formulierkop>

        {verlopen && !fout && (
          <Alert.Root status="info" role="status">
            <Alert.Indicator />
            <Alert.Title fontWeight="normal">Je sessie is verlopen. Log opnieuw in.</Alert.Title>
          </Alert.Root>
        )}

        <Field.Root invalid={!!emailFout}>
          <Field.Label fontWeight="semibold" color="gray.800">
            E-mailadres
          </Field.Label>
          <Input
            ref={emailVeld}
            type="email"
            name="email"
            size="lg"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field.ErrorText>{emailFout}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={!!wachtwoordFout}>
          <Flex w="full" justifyContent="space-between" alignItems="baseline" gap="3">
            <Field.Label fontWeight="semibold" color="gray.800">
              Wachtwoord
            </Field.Label>
            <Text as="span" textStyle="sm">
              {/* Het getypte adres gaat mee, zodat het daar niet opnieuw hoeft. */}
              <Naar to="/admin/wachtwoord-vergeten" state={{ email }}>
                Wachtwoord vergeten?
              </Naar>
            </Text>
          </Flex>
          <WachtwoordInvoer
            ref={wachtwoordVeld}
            name="wachtwoord"
            autoComplete="current-password"
            value={wachtwoord}
            onChange={(e) => setWachtwoord(e.target.value)}
          />
          <Field.ErrorText>{wachtwoordFout}</Field.ErrorText>
        </Field.Root>

        {fout && <Formulierfout melding={fout} />}

        <Button type="submit" colorPalette="ameBlue" size="lg" loading={bezig}>
          Inloggen
        </Button>

        <Voetregel>
          Ben je speler? <Naar to="/">Naar het inlogscherm voor spelers</Naar>
        </Voetregel>
      </chakra.form>
    </Inlogkader>
  );
}
